import { Divider, Flex, Group, IconFont, Select, Text } from '@byecode/ui'
import type {
    AvailableFieldBlockInfo,
    Field,
    FieldBlockAbstract,
    FieldInputConfigProtocol,
    FieldInputType,
    FieldType,
    FormContainerBlockAbstract,
    SubFormColumn,
    TypeInstanceMap
} from '@lighthouse/core'
import { BlockType, CALENDAR_TIME_ZONE, VariableType } from '@lighthouse/core'
import {
    FieldInputNameMap,
    getDateVariableOption,
    getInputInnerTypeMatch,
    getInputVariableOption,
    isRichTextValue,
    ListItem4ByecodeUi,
    RichTextEditor,
    useAtomAction,
    useAtomData,
    VariableSelect
} from '@lighthouse/shared'
import { find, findIndex, reduce } from 'rambda'
import React, { useCallback, useDeferredValue, useMemo } from 'react'
import { Controller, useFormContext } from 'react-hook-form'

import {
    availableFieldBlocksAtom,
    blockHighlightAtom,
    lastPageOfStackAtom,
    pageInternalStateAtomFamily,
    selectedBlockContextAtom,
    selectedBlockFormContainerAtom
} from '@/atoms/page/state'
import { useCurrentAppId, useCurrentEnvId } from '@/hooks/useApplication'
import { useDataSource, useDataSourceList } from '@/hooks/useDataSource'
import { useGetPageParameterAndVariableOptions, useGetUserRecordOptions } from '@/hooks/usePage'
import { useVariableCustomViewOption } from '@/hooks/useVariableCustomViewOption'

import { FieldSelect } from '../../../../FieldSelect'
import { CommonOpera, Editable } from '../../UserOperate'
import { AutoFocus } from '../../UserOperate/AutoFocus'
import { EVENTS_ENTER_FIELDS, FieldInputDescMap, relativeSelectFieldList } from '../constants'
import type { FieldInputFormSlice } from '../formSlice'
import { getSaveFieldOptions, isDisabledFieldInput } from '../helps'
import type { BaseFieldInputSetting, DataMode, FieldOptions } from '../types'
import { User } from '../User'
import { CascadeConfig, CascadeOperation } from './CascadeConfig'
import PersonConfig from './PersonConfig'
import { PhoneNumberConfig } from './PhoneNumberConfig'
import RelativeSelectConfig from './RelativeSelectConfig'
import SliderConfig from './SliderConfig'
import { TextConfig } from './TextConfig'

const TEXT_DATE_FORMAT_OPTIONS = [
    {
        label: '年/月/日 00:00',
        value: 'yyyy/MM/dd HH:mm'
    },
    {
        label: '年-月-日 00:00',
        value: 'yyyy-MM-dd HH:mm'
    },
    {
        label: '日-月-年 00:00',
        value: 'dd-MM-yyyy HH:mm'
    },
    {
        label: '年/月/日',
        value: 'yyyy/MM/dd'
    },
    {
        label: '年-月-日',
        value: 'yyyy-MM-dd'
    },
    {
        label: '日/月/年',
        value: 'dd/MM/yyyy'
    },
    {
        label: '年/月',
        value: 'yyyy/MM'
    },
    {
        label: '年-月',
        value: 'yyyy-MM'
    },
    {
        label: '年',
        value: 'yyyy'
    },
    {
        label: '时分',
        value: 'HH:mm'
    },
    {
        label: '农历-年月日 00:00',
        value: 'lunar-yyyy/MM/dd HH:mm'
    },
    {
        label: '农历-年月日 时辰',
        value: 'lunar-yyyy/MM/dd lunar-HH:mm'
    },
    {
        label: '农历-年月日',
        value: 'lunar-yyyy/MM/dd'
    },
    {
        label: '农历-时辰',
        value: 'lunar-HH:mm'
    }
]

export const TEXT_CALENDAR_FORMAT_OPTIONS = [
    {
        label: '年/月/日',
        value: 'yyyy/MM/dd'
    },
    {
        label: '年-月-日',
        value: 'yyyy-MM-dd'
    },
    {
        label: '日/月/年',
        value: 'dd/MM/yyyy'
    }
]

interface DataProps extends BaseFieldInputSetting {
    mode: DataMode
    id: string
    fieldOptions?: FieldOptions
}

const modeNameMap: Record<DataMode, string> = { field: '页面', form: '表单容器', subForm: '子表单' }

const VARIABLE_FIELD_TYPES = new Set<FieldInputType>([
    'date',
    'email',
    'person',
    'phoneNumber',
    'url',
    'text',
    'relativeSelect',
    'number',
    'checkbox',
    'slider',
    'cascade',
    'calendar'
])

export const Data: React.FunctionComponent<DataProps> = ({ mode, fieldOptions = [], pointer, prefix, id }) => {
    // TODO @张琪 复杂类型导致 ide 卡死，先注释掉，后面优化TS类型
    const { watch, setValue, control } = useFormContext<FieldInputFormSlice>()
    const [inputType, fieldPointer = '', canEdit, dateFormat] = watch([
        `${prefix}inputType`,
        `${prefix}fieldPointer`,
        `${prefix}canEdit`,
        `${prefix}date.dateFormat`
    ])

    const normalFormat = (dateFormat as string)
        ?.split(' ')
        ?.map(item => item.replace('lunar-', ''))
        ?.join(' ')
    const appId = useCurrentAppId()
    const envId = useCurrentEnvId()
    const dataSourceList = useDataSourceList(appId, envId)
    const { run: setBlockHighlight } = useAtomAction(blockHighlightAtom)
    const isUsedInForm = mode === 'form'
    const isUsedInSubForm = mode === 'subForm'
    const isUsedInField = mode === 'field'
    const isParentNotSubForm = mode !== 'subForm'
    const isNotField = mode !== 'field'

    const dataSource = useDataSource(appId, envId, pointer)

    const field = dataSource?.schema?.[fieldPointer ?? '']

    const disableEdit = useMemo(() => isDisabledFieldInput(fieldPointer, fieldOptions), [fieldOptions, fieldPointer])

    const { stackId, pageId } = useAtomData(
        lastPageOfStackAtom,
        useCallback(s => ({ stackId: s?.stackId || '', pageId: s?.pageId || '' }), [])
    )
    const selectNode = useAtomData(
        pageInternalStateAtomFamily(stackId),
        useCallback(s => s?.selectedNodes?.[0], [])
    )
    const [saveField, showRelativeSetting] = useMemo(() => {
        const dsField = dataSource?.schema?.[fieldPointer]
        const isRelativeSelect = dsField && !relativeSelectFieldList.has(dsField.type) && inputType === 'relativeSelect'
        return [dsField, isRelativeSelect]
    }, [dataSource?.schema, fieldPointer, inputType])

    const isShowDefaultValue = (isUsedInForm || isUsedInSubForm) && saveField

    const variableField = useMemo(() => {
        if (!saveField) {
            return
        }

        // @灿白日期输入框强制绑定日期
        if (inputType === 'date' || inputType === 'calendar') {
            return saveField.type === 'date'
                ? saveField
                : ({
                      type: 'date',
                      date: { format: normalFormat },
                      id: saveField.id,
                      name: saveField.name,
                      dsId: saveField.dsId,
                      dsName: saveField.dsName,
                      innerType: saveField.innerType
                  } satisfies Field)
        }
        return saveField
    }, [inputType, saveField, normalFormat])

    const validateFieldType = useCallback(
        (field: Field | TypeInstanceMap) => {
            // @灿白  日期输入框强制绑定日期
            if (inputType === 'date' || inputType === 'calendar') {
                return typeof field === 'object' ? field.innerType === 'DATE' : field === 'DATE'
            }
            return typeof field === 'object' ? field.innerType === saveField?.innerType : field === saveField?.innerType
        },
        [inputType, saveField?.innerType]
    )

    const allUerOption = useGetUserRecordOptions()

    const userOption = useMemo(() => {
        return allUerOption
    }, [allUerOption])

    const dateOption = useMemo(
        () =>
            inputType === 'date' || inputType === 'calendar' ? getDateVariableOption(['TODAY', 'TOMORROW', 'YESTERDAY', 'NOW']) : undefined,
        [inputType]
    )

    const handleMouseEnter = useCallback(
        (id: string) => {
            setBlockHighlight([id])
            document.querySelector(`div[data-node-id="${id}"]`)?.scrollIntoView({ behavior: 'smooth', block: 'center' })
        },
        [setBlockHighlight]
    )

    const handleMouseLeave = useCallback(() => {
        setBlockHighlight([])
    }, [setBlockHighlight])

    // 直接从 blockContextIndex 获取，避免递归查找
    const parentForm = useAtomData(selectedBlockFormContainerAtom)
    const selectedBlock = useAtomData(
        selectedBlockContextAtom,
        useCallback(ctx => ctx?.block, [])
    )

    const availableFieldBlocksInfo = useDeferredValue(useAtomData(availableFieldBlocksAtom))

    const allPageOptions = useGetPageParameterAndVariableOptions({
        pageId
    })

    const inputOption = useMemo(() => {
        if (availableFieldBlocksInfo.length === 0) {
            return
        }
        return getInputVariableOption(availableFieldBlocksInfo, dataSourceList, {
            onMouseEnter: handleMouseEnter,
            onMouseLeave: handleMouseLeave
        })
    }, [availableFieldBlocksInfo, dataSourceList, handleMouseEnter, handleMouseLeave])

    const [saveFieldOptions, disableSaveFiledList]: [FieldOptions, FieldType[]] = useMemo(
        () => getSaveFieldOptions(inputType, fieldOptions),
        [fieldOptions, inputType]
    )

    const finalFieldBlocksWithDsId = useMemo(() => {
        if (isUsedInField) {
            return
        }
        if (isUsedInSubForm) {
            if (selectedBlock && selectedBlock.type === 'subForm') {
                const subFormBlock = selectedBlock
                const {
                    id: subFormId,
                    title: subFormTitle,
                    config: { columns, pointer }
                } = subFormBlock
                // 创建一个虚拟的 formContainer 以便 getInputVariableOption 能获取到 dsId
                const virtualFormContainer = {
                    id: subFormId,
                    type: BlockType.formContainer,
                    title: subFormTitle,
                    config: { pointer }
                } as FormContainerBlockAbstract
                return reduce<SubFormColumn, AvailableFieldBlockInfo[]>(
                    (p, c) => {
                        const { config: columnConfig, id: subFormFieldInputId } = c
                        const { initialValue, title = '' } = columnConfig
                        if (initialValue && initialValue.type === VariableType.SELECT_DATASOURCE) {
                            return p
                        }
                        if (subFormFieldInputId !== id) {
                            // 构建符合 BlockRuntimeContext 结构的对象
                            const newFieldBlock: AvailableFieldBlockInfo = {
                                block: {
                                    id: subFormFieldInputId,
                                    type: BlockType.field,
                                    title,
                                    config: columnConfig
                                } as FieldBlockAbstract,
                                uniqueKey: subFormFieldInputId,
                                formContainer: virtualFormContainer
                            }
                            return [...p, newFieldBlock]
                        }
                        return p
                    },
                    [],
                    columns
                )
            }
        }
        return availableFieldBlocksInfo.filter(info => {
            if (info.block.type === 'onOff') {
                return true
            }
            const {
                block: {
                    id,
                    config: { initialValue }
                },
                formContainer
            } = info
            if (initialValue && initialValue.type === VariableType.SELECT_DATASOURCE) {
                return false
            }
            return id !== selectNode?.id && formContainer?.id === parentForm?.id
        })
    }, [isUsedInField, isUsedInSubForm, availableFieldBlocksInfo, selectNode?.id, selectedBlock, id, parentForm?.id])

    // 当前输入框被作为其他输入框默认依据时，不能使用查询数据表变量
    const selectDataSourceVariableEnable = useMemo(() => {
        if (mode === 'subForm') {
            if (selectedBlock && selectedBlock.type === 'subForm') {
                const {
                    config: { columns, pointer }
                } = selectedBlock

                return columns.every(fieldBlock => {
                    const {
                        config: { initialValue }
                    } = fieldBlock
                    if (initialValue && initialValue.type === VariableType.SELECT_DATASOURCE) {
                        return !initialValue?.selectDataSourceVariable?.filter.expression?.conditions?.some(condition => {
                            const variable = condition?.paramList?.[0]
                            return variable?.inputVariable?.blockId === id
                        })
                    }
                    return true
                })
            }
            return false
        }
        return availableFieldBlocksInfo.every(info => {
            if (info.block.type === 'onOff') {
                return true
            }
            const {
                block: {
                    config: { initialValue }
                }
            } = info
            if (initialValue && initialValue.type === VariableType.SELECT_DATASOURCE) {
                return !initialValue?.selectDataSourceVariable?.filter.expression?.conditions?.some(condition => {
                    const variable = condition?.paramList?.[0]
                    if (selectNode?.scope) {
                        return variable?.inputVariable?.blockId === selectNode?.id && variable?.inputVariable?.scope === selectNode?.scope
                    }
                    return variable?.inputVariable?.blockId === selectNode?.id
                })
            }
            return true
        })
    }, [selectedBlock, availableFieldBlocksInfo, id, mode, selectNode?.id, selectNode?.scope])

    const { customViewOption } = useVariableCustomViewOption()

    const selectDataSourceProps = useMemo(() => {
        if (!selectDataSourceVariableEnable) {
            return
        }
        return {
            inputOption: getInputVariableOption(finalFieldBlocksWithDsId ?? [], dataSourceList),
            userOption: allUerOption,
            pageOptions: allPageOptions,
            validateFieldType
        }
    }, [selectDataSourceVariableEnable, finalFieldBlocksWithDsId, dataSourceList, allUerOption, allPageOptions, validateFieldType])

    const isShowAllowMultiple = useMemo(() => {
        if (canEdit) {
            if (mode === 'field') {
                return inputType === 'relativeSelect'
            }
            if (mode === 'form' || mode === 'subForm') {
                return field && inputType === 'relativeSelect' && field.type === 'text'
            }
        }
        return false
    }, [canEdit, field, inputType, mode])

    const operationEle = useMemo(() => {
        return (
            <>
                {isNotField && isShowDefaultValue && VARIABLE_FIELD_TYPES.has(inputType) && (
                    <ListItem4ByecodeUi alignItems="center" justifyContent="space-between">
                        <Text>默认值</Text>
                        <Controller
                            name={`${prefix}initialValue`}
                            control={control}
                            render={({ field: { value, onChange } }) => (
                                <VariableSelect
                                    innerTypes={getInputInnerTypeMatch(variableField?.innerType)}
                                    field={variableField}
                                    userOption={userOption}
                                    dateOption={dateOption}
                                    inputOption={inputOption}
                                    pageOptions={allPageOptions}
                                    viewOptions={customViewOption ? [customViewOption] : undefined}
                                    value={value}
                                    enableFormula
                                    disablePexels
                                    dataSourceList={dataSourceList}
                                    selectDataSourceProps={selectDataSourceProps}
                                    onChange={val => {
                                        onChange(val)
                                    }}
                                />
                            )}
                        />
                    </ListItem4ByecodeUi>
                )}
                {isNotField && isShowDefaultValue && inputType === 'notes' && (
                    <Group
                        mode="none"
                        styles={{
                            label: {
                                fontWeight: 'unset !important'
                            }
                        }}
                        label="默认值"
                    >
                        <Controller
                            name={`${prefix}initialValue`}
                            control={control}
                            render={({ field: { value, onChange } }) => {
                                const initialValue = value && value?.type === VariableType.VALUE ? value?.valueVariable?.value : ''
                                const richTextValue = isRichTextValue(initialValue) ? initialValue : null
                                return (
                                    <RichTextEditor
                                        styles={{
                                            editorContainer: {
                                                border: '1px solid var(--color-gray-200)',
                                                borderRadius: 8,
                                                marginTop: 4
                                            },
                                            editorContent: {
                                                backgroundColor: 'var(--color-gray-50)'
                                            }
                                        }}
                                        minHeight={200}
                                        config={{
                                            mode: 'all'
                                        }}
                                        value={richTextValue}
                                        // autofocus="end"
                                        onChange={val => onChange({ type: VariableType.VALUE, valueVariable: { value: val } })}
                                    />
                                )
                            }}
                        />
                    </Group>
                )}

                {inputType === 'calendar' && (
                    <ListItem4ByecodeUi alignItems="center" justifyContent="space-between">
                        <Text>时区</Text>
                        <Controller
                            name={`${prefix}calendar.timeZone`}
                            control={control}
                            render={({ field: { value, onChange } }) => (
                                <Select
                                    style={{ width: 180 }}
                                    options={[
                                        { label: '本机时区', value: CALENDAR_TIME_ZONE.local },
                                        { label: '北京时区', value: CALENDAR_TIME_ZONE['Asia/Shanghai'] }
                                    ]}
                                    value={value || CALENDAR_TIME_ZONE.local}
                                    onChange={onChange}
                                />
                            )}
                        />
                    </ListItem4ByecodeUi>
                )}
                {isNotField && saveField && saveField.type === 'text' && inputType === 'date' && (
                    <ListItem4ByecodeUi alignItems="center" justifyContent="space-between">
                        <Text>写入格式</Text>
                        <Controller
                            name={`${prefix}date.dateFormat`}
                            control={control}
                            render={({ field: { value, onChange } }) => (
                                <Select style={{ width: 180 }} options={TEXT_DATE_FORMAT_OPTIONS} value={value} onChange={onChange} />
                            )}
                        />
                    </ListItem4ByecodeUi>
                )}
                {isNotField && saveField && saveField.type === 'text' && inputType === 'calendar' && (
                    <ListItem4ByecodeUi alignItems="center" justifyContent="space-between">
                        <Text>写入格式</Text>
                        <Controller
                            name={`${prefix}calendar.dateFormat`}
                            control={control}
                            render={({ field: { value, onChange } }) => (
                                <Select style={{ width: 180 }} options={TEXT_CALENDAR_FORMAT_OPTIONS} value={value} onChange={onChange} />
                            )}
                        />
                    </ListItem4ByecodeUi>
                )}
                {isParentNotSubForm && (
                    <Controller
                        name={`${prefix}canEdit`}
                        render={({ field }) => (
                            <Editable
                                disabled={disableEdit}
                                value={field.value}
                                onChange={val => {
                                    field.onChange(val)
                                    !val && setValue(`${prefix}required`, false)
                                    !val && setValue(`${prefix}autofocus`, false)
                                }}
                            />
                        )}
                    />
                )}
                {canEdit && EVENTS_ENTER_FIELDS.has(inputType) && (
                    <Controller
                        name={`${prefix}autofocus`}
                        render={({ field }) => (
                            <AutoFocus
                                value={field.value}
                                onChange={val => {
                                    field.onChange(val)
                                }}
                            />
                        )}
                    />
                )}
                {inputType === 'person' && <PersonConfig prefix={prefix} />}

                {isShowAllowMultiple && (
                    <Controller
                        name={`${prefix}${inputType}.canMultipleChoice`}
                        render={({ field }) => <CommonOpera label="多选" value={field.value} onChange={field.onChange} />}
                    />
                )}
                {saveField && inputType === 'cascade' && <CascadeOperation prefix={prefix} />}
            </>
        )
    }, [
        isNotField,
        isShowDefaultValue,
        inputType,
        prefix,
        control,
        saveField,
        isParentNotSubForm,
        canEdit,
        isShowAllowMultiple,
        variableField,
        userOption,
        dateOption,
        inputOption,
        allPageOptions,
        customViewOption,
        dataSourceList,
        selectDataSourceProps,
        disableEdit,
        setValue
    ])

    const isOperationChildren = operationEle.props.children.some(Boolean)

    return (
        <>
            {isNotField && (
                <>
                    <Group label="字段">
                        <ListItem4ByecodeUi alignItems="center" justifyContent="space-between">
                            <Text>写入字段</Text>
                            <Controller
                                name={`${prefix}fieldPointer`}
                                render={({ field }) => {
                                    return (
                                        <FieldSelect
                                            options={saveFieldOptions}
                                            position="bottom-end"
                                            value={field.value}
                                            dsId={pointer}
                                            onChange={val => {
                                                field.onChange(val)
                                                const index = findIndex(item => item.value === val, saveFieldOptions)
                                                index !== -1 && setValue(`${prefix}title`, saveFieldOptions[index].label)
                                                // const disabledEdit = isDisabledFieldInput(val, fieldOptions)
                                                // if (disabledEdit) {
                                                // setValue(`${prefix}canEdit`, false)
                                                // setValue(`${prefix}required`, false)
                                                // }
                                                const fieldType = find(item => item.value === val, saveFieldOptions)?.type
                                                if (inputType === 'relativeSelect') {
                                                    if (fieldType === 'number') {
                                                        setValue(`${prefix}${inputType}.canMultipleChoice`, false)
                                                    }
                                                    if (fieldType === 'role') {
                                                        setValue(`${prefix}${inputType}.canMultipleChoice`, false)
                                                        setValue(`${prefix}${inputType}.relativePointer`, 'ROLE_DATASOURCE')
                                                        setValue(`${prefix}${inputType}.relativeFieldPointer`, 'ID')
                                                        setValue(`${prefix}${inputType}.relativeShowFieldPointer`, 'ROLE_NAME')
                                                        setValue(`${prefix}${inputType}.showType`, 'list')
                                                    }
                                                }
                                            }}
                                            disableTitle={
                                                saveFieldOptions.length > 0
                                                    ? `${
                                                          FieldInputNameMap[inputType as FieldInputConfigProtocol['inputType']]
                                                      }输入框不支持以下字段：`
                                                    : ''
                                            }
                                            disableList={saveFieldOptions.length > 0 ? disableSaveFiledList : []}
                                            dropdownWidth={250}
                                            emptyComponent={
                                                <Flex
                                                    style={{ height: 100, padding: '0 20px' }}
                                                    direction="column"
                                                    alignItems="center"
                                                    justifyContent="center"
                                                    gap={6}
                                                >
                                                    <IconFont size={24} color="var(--color-gray-500)" type="Nodata-8i554976" />
                                                    <Text size={14} color="var(--color-gray-500)" whiteSpace="break-spaces" align="center">
                                                        {FieldInputDescMap[inputType as FieldInputConfigProtocol['inputType']]}
                                                    </Text>
                                                </Flex>
                                            }
                                        />
                                    )
                                }}
                            />
                        </ListItem4ByecodeUi>
                    </Group>
                    <Divider color="var(--color-gray-200)" />
                </>
            )}
            {inputType === 'relativeSelect' && ((saveField && !relativeSelectFieldList.has(saveField.type)) || mode === 'field') && (
                <>
                    <RelativeSelectConfig mode={mode} fieldOptions={fieldOptions} field={saveField} prefix={prefix} />
                    <Divider color="var(--color-gray-200)" />
                </>
            )}
            {saveField && inputType === 'slider' && (
                <>
                    <SliderConfig prefix={prefix} />
                    <Divider color="var(--color-gray-200)" />
                </>
            )}
            {inputType === 'cascade' && (
                <>
                    <CascadeConfig pointer={pointer} prefix={prefix} />
                    <Divider color="var(--color-gray-200)" />
                </>
            )}
            {isOperationChildren && (
                <>
                    <Group label="输入">{operationEle}</Group>
                    <Divider color="var(--color-gray-200)" />
                </>
            )}

            <User pointer={pointer} mode={mode} prefix={prefix} />
            <Divider color="var(--color-gray-200)" />

            {saveField && inputType === 'phoneNumber' && (
                <>
                    <PhoneNumberConfig prefix={prefix} />
                    <Divider style={{ margin: 0 }} color="var(--color-gray-200)" />
                </>
            )}
            {saveField && isParentNotSubForm && canEdit && inputType === 'text' && (
                <>
                    <TextConfig prefix={prefix} />
                    <Divider style={{ margin: 0 }} color="var(--color-gray-200)" />
                </>
            )}
        </>
    )
}
